import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { OptionItem, OptionList } from '../contacts/filters/OptionList';
import { Target } from './tagging';

export interface TargetChoice {
  id: number;
  name: string;
  /** Tags: the dot's colour. Lists: absent. */
  color?: string | null;
  /** Shown on the right: how many of the selection have it, or how many in the CRM. */
  count?: number;
}

interface TargetPickerProps {
  /** "tag" or "list": names the search box, the create button and the list for screen readers. */
  noun: 'tag' | 'list';
  choices: ReadonlyArray<TargetChoice>;
  selected: ReadonlyArray<Target>;
  onChange: (next: Array<Target>) => void;
  /** Tags: several at once, up to this many. Lists: one. */
  max: number;
  /** Offer "Create tag “…”" when the typed name matches none. */
  allowCreate: boolean;
  /** Told the search text, for choices the API has to find (more lists than were loaded). */
  onQueryChange?: (text: string) => void;
  loading?: boolean;
  emptyText?: string;
  /** Under the list: what the numbers mean. */
  footer?: React.ReactNode;
}

const keyOf = (target: Target) => (target.id !== undefined ? String(target.id) : `new:${target.name.toLowerCase()}`);

/** Typed names are cleaned as the API cleans them: trimmed, runs of spaces made one. */
export const cleanName = (text: string) => text.trim().replace(/\s+/g, ' ');

/**
 * Picks the tags (several) or the list (one) a change applies, from a searchable
 * checklist with counts. A name that matches none can be made: it is listed as
 * chosen and made when the change is confirmed, so cancelling makes nothing.
 */
export const TargetPicker = ({
  noun,
  choices,
  selected,
  onChange,
  max,
  allowCreate,
  onQueryChange,
  loading,
  emptyText,
  footer,
}: TargetPickerProps) => {
  const [query, setQuery] = useState('');
  const single = max === 1;
  const full = !single && selected.length >= max;

  const typed = cleanName(query);
  const exists =
    choices.some((choice) => choice.name.toLowerCase() === typed.toLowerCase()) ||
    selected.some((target) => target.name.toLowerCase() === typed.toLowerCase());
  const canCreate = allowCreate && typed.length > 0 && typed.length <= 100 && !exists && !full;

  // Chosen but not among the choices: one still to make, or one made a moment ago.
  const extra = selected.filter((target) => target.id === undefined || !choices.some((choice) => choice.id === target.id));

  const items: Array<OptionItem> = useMemo(() => {
    const q = typed.toLowerCase();
    const shown = choices.filter((choice) => !q || choice.name.toLowerCase().includes(q));
    return [
      ...extra.map((target) => ({
        value: keyOf(target),
        label: target.name,
        ...(target.id === undefined ? { note: 'new' } : {}),
        ...(noun === 'tag' ? { color: null } : {}),
      })),
      ...shown.map((choice) => ({
        value: String(choice.id),
        label: choice.name,
        count: choice.count,
        ...(noun === 'tag' ? { color: choice.color ?? null } : {}),
      })),
    ];
  }, [choices, extra, typed, noun]);

  const byKey = new Map<string, Target>([
    ...choices.map((choice): [string, Target] => [String(choice.id), { id: choice.id, name: choice.name, have: choice.count }]),
    ...extra.map((target): [string, Target] => [keyOf(target), target]),
  ]);

  const toggle = (value: string) => {
    const chosen = selected.some((target) => keyOf(target) === value);
    if (chosen) {
      onChange(selected.filter((target) => keyOf(target) !== value));
      return;
    }
    const target = byKey.get(value);
    if (!target) return;
    if (single) onChange([target]);
    else if (!full) onChange([...selected, target]);
  };

  const create = () => {
    const target: Target = { name: typed };
    onChange(single ? [target] : [...selected, target]);
    setQuery('');
    onQueryChange?.('');
  };

  return (
    <Box>
      <OptionList
        label={noun === 'tag' ? 'Tags' : 'Lists'}
        items={items}
        selected={selected.map(keyOf)}
        onToggle={toggle}
        single={single}
        searchPlaceholder={allowCreate ? `Find or create a ${noun}` : `Find a ${noun}`}
        query={query}
        onQueryChange={(text) => {
          setQuery(text);
          onQueryChange?.(text);
        }}
        loading={loading}
        maxHeight={280}
        emptyText={emptyText ?? (allowCreate ? `No ${noun} by that name.` : 'Nothing matches.')}
        footer={
          <>
            {canCreate && (
              <Button size="small" startIcon={<AddIcon />} onClick={create} sx={{ mt: 1, textTransform: 'none' }}>
                Create {noun} “{typed}”
              </Button>
            )}
            {full && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                At most {max} {noun}s in one change.
              </Typography>
            )}
            {footer}
          </>
        }
      />
    </Box>
  );
};
